import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Check, Clock, AlertTriangle, Lock, RotateCcw, ExternalLink, Calendar as CalIcon, User, MapPin, Sparkles, ArrowRight } from "lucide-react";
import { toast, Toaster } from "sonner";
import { api } from "@/lib/api";
import { formatPrice, formatMoney, formatFullDate } from "@/lib/format";
import { GoldRule, Wordmark } from "@/components/Brand";
import { PrimaryButton, Skeleton } from "@/components/BookingShell";
import { stashBookingResume } from "@/context/BookingContext";

/**
 * Branded payment return pages.
 *
 *   /booking/payment-success   — Stripe returned; verify with Amelia
 *   /booking/payment-cancelled — customer bailed out of Stripe
 *   /booking/payment-pending   — booking exists but payment link failed / not yet paid
 *
 * All three take `?appointment_id=<int>&token=<hmac>` and never trust the
 * redirect alone as proof of payment — they call the public sync endpoint
 * which reads Amelia's canonical record.
 */

const PHONE_HREF = "https://wa.me/447000000000";  // fallback contact — swap to real WhatsApp / phone when known

function useBookingParams() {
    const [sp] = useSearchParams();
    const appointmentId = Number(sp.get("appointment_id") || 0);
    let token = sp.get("token") || "";
    // Fallback to sessionStorage if the redirect lost the token
    if (!token || !appointmentId) {
        try {
            const stashed = JSON.parse(sessionStorage.getItem("pout_last_booking") || "null");
            if (stashed) {
                return {
                    appointmentId: appointmentId || stashed.appointment_id,
                    token: token || stashed.booking_token,
                    stashed,
                };
            }
        } catch { /* ignore */ }
    }
    return { appointmentId, token, stashed: null };
}

function Layout({ children }) {
    return (
        <>
            <Toaster theme="dark" position="top-center" />
            <div className="relative min-h-[100dvh] flex flex-col" data-testid="payment-return-page">
                <header className="sticky top-0 z-30 glass-strong" style={{ paddingTop: "env(safe-area-inset-top)" }}>
                    <div className="max-w-xl mx-auto px-5 py-3.5 flex items-center justify-center gap-3">
                        <Wordmark small />
                    </div>
                </header>
                <main className="flex-1 relative">
                    <div className="max-w-xl mx-auto px-5 pt-8 pb-16 animate-fade-up">{children}</div>
                </main>
            </div>
        </>
    );
}

function StatusBadge({ tone, icon: Icon, label }) {
    const toneStyle = tone === "gold"
        ? "border-gold/60 bg-gold/10 text-gold"
        : tone === "amber"
        ? "border-amber-400/50 bg-amber-500/10 text-amber-100"
        : tone === "red"
        ? "border-red-400/50 bg-red-500/10 text-red-100"
        : "border-white/20 bg-white/5 text-white/70";
    return (
        <div className={`mx-auto h-20 w-20 rounded-full glass flex items-center justify-center border ${toneStyle}`}>
            <Icon className="h-9 w-9" strokeWidth={1.4} />
        </div>
    );
}

function SummaryCard({ data }) {
    if (!data) return null;
    const startsAt = data.starts_at ? new Date(data.starts_at.replace(" ", "T")) : null;
    const dateLabel = startsAt ? formatFullDate(startsAt.toISOString().slice(0, 10)) : "—";
    const timeLabel = startsAt ? startsAt.toTimeString().slice(0, 5) : "—";
    const customerName = [data.customer_first_name, data.customer_last_name].filter(Boolean).join(" ") || "—";
    return (
        <div className="rounded-3xl glass p-6 text-left space-y-4" data-testid="payment-summary-card">
            <div>
                <div className="text-[10px] uppercase tracking-[0.24em] text-white/45 flex items-center gap-2">
                    <User className="h-3 w-3 text-gold" strokeWidth={1.5} /> Booked for
                </div>
                <div className="mt-1 text-white font-ui text-base" data-testid="payment-customer-name">{customerName}</div>
            </div>
            <div>
                <div className="text-[10px] uppercase tracking-[0.24em] text-white/45 flex items-center gap-2">
                    <Sparkles className="h-3 w-3 text-gold" strokeWidth={1.5} /> Treatment
                </div>
                <div className="mt-1 font-serif text-2xl text-white leading-snug" data-testid="payment-service-name">
                    {data.service_name || "—"}
                </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.24em] text-white/45 flex items-center gap-2">
                        <CalIcon className="h-3 w-3 text-gold" strokeWidth={1.5} /> Date
                    </div>
                    <div className="mt-1 text-white font-ui text-sm" data-testid="payment-date">{dateLabel}</div>
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-[0.24em] text-white/45 flex items-center gap-2">
                        <Clock className="h-3 w-3 text-gold" strokeWidth={1.5} /> Time
                    </div>
                    <div className="mt-1 text-white font-ui text-sm" data-testid="payment-time">{timeLabel}</div>
                </div>
            </div>
            <div>
                <div className="text-[10px] uppercase tracking-[0.24em] text-white/45 flex items-center gap-2">
                    <MapPin className="h-3 w-3 text-gold" strokeWidth={1.5} /> Practitioner
                </div>
                <div className="mt-1 text-white font-ui text-sm">Angela</div>
            </div>
            <GoldRule className="py-1" />
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.24em] text-gold/85">Deposit paid</div>
                    <div className="mt-1 font-serif text-2xl gold-text" data-testid="payment-deposit-paid">
                        {formatMoney(data.deposit_paid || data.amount_paid_total || 0)}
                    </div>
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-[0.24em] text-white/45">Balance at appointment</div>
                    <div className="mt-1 font-ui text-base text-white/80" data-testid="payment-remaining">
                        {formatMoney(data.remaining_balance || 0)}
                    </div>
                </div>
            </div>
            {data.reference && (
                <div>
                    <div className="text-[10px] uppercase tracking-[0.24em] text-white/45">Booking reference</div>
                    <div className="mt-1 text-white/70 font-mono text-sm" data-testid="payment-reference">{data.reference}</div>
                </div>
            )}
            <div>
                <div className="text-[10px] uppercase tracking-[0.24em] text-white/45">Booking status</div>
                <div className="mt-1 text-white/85 font-ui text-sm" data-testid="payment-status-line">
                    {(data.appointment_status || "").replace(/_/g, " ") || "—"}{" · "}
                    Payment: <span className={data.payment_status === "paid" ? "text-gold" : "text-amber-200"}>
                        {data.payment_status || "—"}
                    </span>
                </div>
            </div>
        </div>
    );
}

/* ==========================================================================
 * SUCCESS
 * ======================================================================= */
export function BookingPaymentSuccess() {
    const { appointmentId, token } = useBookingParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [attempts, setAttempts] = useState(0);

    const load = useCallback(async () => {
        if (!appointmentId || !token) { setError(new Error("Missing booking reference.")); return; }
        setError(null);
        try {
            const r = await api.getPublicPayment(appointmentId, token);
            setData(r);
        } catch (e) { setError(e); }
    }, [appointmentId, token]);

    useEffect(() => { load(); }, [load]);

    // Auto-poll while payment_status !== "paid"
    useEffect(() => {
        if (!data) return undefined;
        if ((data.payment_status || "").toLowerCase() === "paid") return undefined;
        if (attempts > 15) return undefined; // ~2 min total
        const t = setTimeout(async () => {
            setAttempts((n) => n + 1);
            await load();
        }, 6000);
        return () => clearTimeout(t);
    }, [data, attempts, load]);

    const paid = (data?.payment_status || "").toLowerCase() === "paid";
    const stillPending = data && !paid;

    const manualSync = async () => {
        setBusy(true);
        try { await load(); toast.success("Refreshed from Amelia"); }
        catch (e) { toast.error(e.message || "Could not refresh"); }
        finally { setBusy(false); }
    };

    return (
        <Layout>
            <div className="text-center">
                <StatusBadge
                    tone={paid ? "gold" : "amber"}
                    icon={paid ? Check : Clock}
                    label={paid ? "Paid" : "Confirming"}
                />
                <div className="mt-8">
                    <div className="text-[10px] uppercase tracking-[0.28em] text-gold/85" data-testid="payment-headline-eyebrow">
                        {paid ? "Deposit confirmed" : "Confirming payment"}
                    </div>
                    <h1 className="mt-2 font-serif font-light text-4xl sm:text-5xl leading-[1.05] text-white" data-testid="payment-headline">
                        {paid ? <>Booking <span className="gold-text">received</span></> : <>Payment <span className="gold-text">confirming</span></>}
                    </h1>
                    <p className="mt-4 text-white/60 text-sm max-w-md mx-auto" data-testid="payment-subcopy">
                        {paid
                            ? <>Thank you {data?.customer_first_name || ""}. Your appointment with Angela is confirmed and Amelia has your deposit on record.</>
                            : <>Your payment is being confirmed. Please do not make another payment. We're checking with Amelia now — this usually takes just a moment.</>}
                    </p>
                </div>

                {stillPending && (
                    <p className="mt-4 text-[11px] text-white/45" data-testid="payment-pending-hint">
                        Checking again in a few seconds… (attempt {attempts + 1})
                    </p>
                )}
            </div>

            {error && (
                <div className="mt-8 rounded-2xl border border-red-400/40 bg-red-500/5 p-4 text-center" data-testid="payment-error">
                    <AlertTriangle className="h-5 w-5 text-red-300 mx-auto mb-2" strokeWidth={2} />
                    <p className="text-sm text-red-100">{error.message}</p>
                    <button onClick={load} className="mt-3 h-10 px-4 rounded-full border border-red-300/40 text-red-100 text-xs uppercase tracking-[0.18em] inline-flex items-center gap-1">
                        <RotateCcw className="h-3 w-3" /> Try again
                    </button>
                </div>
            )}

            {!data && !error && <div className="mt-8"><Skeleton className="h-56" /></div>}

            {data && <div className="mt-8"><SummaryCard data={data} /></div>}

            {stillPending && (
                <div className="mt-4 text-center">
                    <button onClick={manualSync} disabled={busy}
                            data-testid="payment-check-again"
                            className="h-11 px-5 rounded-full border hairline text-white/85 text-sm inline-flex items-center gap-2 hover:text-gold hover:border-gold/60 disabled:opacity-40">
                        <RotateCcw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} /> Check payment status
                    </button>
                </div>
            )}

            {paid && data?.consultation_link && (
                <div className="mt-8 text-center">
                    <p className="text-sm text-white/70 mb-4">
                        Please complete your forms before your visit.
                    </p>
                    <a
                        href={data.consultation_link}
                        data-testid="payment-open-consultation"
                        className="inline-block"
                    >
                        <PrimaryButton>Complete your forms</PrimaryButton>
                    </a>
                </div>
            )}

            {paid && !data?.consultation_link && (
                <div className="mt-8 text-center">
                    <p className="text-sm text-white/70">
                        {data.forms_pending ? "Your booking is confirmed. Your forms are still being prepared; please check again shortly." : "Your booking is confirmed. We look forward to seeing you."}
                    </p>
                </div>
            )}
        </Layout>
    );
}

/* ==========================================================================
 * CANCELLED
 * ======================================================================= */
export function BookingPaymentCancelled() {
    const { appointmentId, token } = useBookingParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [rescheduling, setRescheduling] = useState(false);

    useEffect(() => {
        if (!appointmentId || !token) { setLoading(false); return; }
        api.getPublicPayment(appointmentId, token)
            .then(setData)
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [appointmentId, token]);

    const retry = async () => {
        if (!appointmentId || !token) { toast.error("Missing booking reference."); return; }
        setBusy(true);
        try {
            let url = data?.payment_link_url;
            if (!url) {
                const r = await api.regenerateBookingPayment(appointmentId, token);
                url = r.payment_link_url;
            }
            if (!url) throw new Error("Could not generate a payment link.");
            window.location.assign(url);
        } catch (e) { toast.error(e.message || "Could not open payment page."); setBusy(false); }
    };

    const reschedule = async () => {
        if (!appointmentId || !token) { toast.error("Missing booking reference."); return; }
        setRescheduling(true);
        try {
            const r = await api.cancelBooking(appointmentId, token);
            // Stash service_id + customer so the fresh booking flow jumps straight
            // to the date-picker with everything pre-filled.
            stashBookingResume({
                serviceId: r.service_id || data?.appointment_id ? r.service_id : null,
                customer: r.customer || {
                    firstName: data?.customer_first_name,
                    lastName: data?.customer_last_name,
                },
            });
            // Clear the leftover "last booking" so returning from Stripe on the
            // NEW booking doesn't pick up stale identifiers.
            try { sessionStorage.removeItem("pout_last_booking"); } catch { /* */ }
            toast.success("Let's find another time");
            navigate("/");
        } catch (e) {
            toast.error(e.message || "Could not release your slot. Please try again.");
            setRescheduling(false);
        }
    };

    return (
        <Layout>
            <div className="text-center">
                <StatusBadge tone="amber" icon={AlertTriangle} />
                <div className="mt-8">
                    <div className="text-[10px] uppercase tracking-[0.28em] text-amber-200/85">Payment cancelled</div>
                    <h1 className="mt-2 font-serif font-light text-4xl leading-[1.05] text-white" data-testid="payment-cancelled-headline">
                        Your appointment is <span className="gold-text">not confirmed</span> yet
                    </h1>
                    <p className="mt-4 text-white/65 text-sm max-w-md mx-auto">
                        We've kept your appointment reserved for you. To lock it in, complete your deposit now — or pick a different time that suits you better.
                    </p>
                </div>
            </div>

            {loading && <div className="mt-8"><Skeleton className="h-56" /></div>}
            {!loading && data && <div className="mt-8"><SummaryCard data={data} /></div>}

            <div className="mt-8 space-y-3">
                <button onClick={retry} disabled={busy || rescheduling} data-testid="payment-retry"
                        className="w-full h-12 rounded-full bg-gold text-navy font-semibold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50">
                    <Lock className="h-4 w-4" /> {busy ? "Opening secure payment…" : "Pay deposit now"}
                </button>
                <button onClick={reschedule} disabled={busy || rescheduling} data-testid="payment-reschedule"
                        className="w-full h-12 rounded-full border hairline text-white/90 hover:text-gold hover:border-gold/60 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50">
                    {rescheduling ? (
                        <>Releasing your slot…</>
                    ) : (
                        <>Try a different time instead <ArrowRight className="h-4 w-4" /></>
                    )}
                </button>
                <p className="text-center text-[11px] text-white/40 pt-1">
                    "Try a different time" cancels this pending booking so we can offer the slot to someone else.
                </p>
            </div>
        </Layout>
    );
}

/* ==========================================================================
 * PENDING / recovery when the initial link couldn't be generated
 * ======================================================================= */
export function BookingPaymentPending() {
    const { appointmentId, token } = useBookingParams();
    const [sp] = useSearchParams();
    const initialFailure = sp.get("err") === "1";
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        if (!appointmentId || !token) { setError(new Error("Missing booking reference.")); return; }
        try { setData(await api.getPublicPayment(appointmentId, token)); setError(null); }
        catch (e) { setError(e); }
    }, [appointmentId, token]);

    useEffect(() => { load(); }, [load]);

    const tryPayment = async () => {
        if (!appointmentId || !token) return;
        setBusy(true);
        try {
            const r = await api.regenerateBookingPayment(appointmentId, token);
            if (r?.payment_link_url) window.location.assign(r.payment_link_url);
            else throw new Error("No payment URL returned.");
        } catch (e) { toast.error(e.message || "Could not generate a payment link."); setBusy(false); }
    };

    return (
        <Layout>
            <div className="text-center">
                <StatusBadge tone="amber" icon={Clock} />
                <div className="mt-8">
                    <div className="text-[10px] uppercase tracking-[0.28em] text-amber-200/85">Appointment reserved</div>
                    <h1 className="mt-2 font-serif font-light text-4xl leading-[1.05] text-white" data-testid="payment-pending-headline">
                        {initialFailure ? (
                            <>We saved your <span className="gold-text">booking</span></>
                        ) : (
                            <>Your <span className="gold-text">deposit</span> is being processed</>
                        )}
                    </h1>
                    <p className="mt-4 text-white/65 text-sm max-w-md mx-auto">
                        {initialFailure
                            ? "Your appointment is safely reserved, but we hit a snag creating the secure payment link. Try again — we'll never charge you twice."
                            : "Your payment is being confirmed. Please do not make another payment. If nothing changes in a minute, tap below to double-check with Amelia."}
                    </p>
                </div>
            </div>

            {data && <div className="mt-8"><SummaryCard data={data} /></div>}
            {error && <p className="mt-6 text-center text-red-200 text-sm">{error.message}</p>}

            <div className="mt-8 space-y-3">
                <button onClick={tryPayment} disabled={busy || !appointmentId} data-testid="payment-try-again"
                        className="w-full h-12 rounded-full bg-gold text-navy font-semibold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50">
                    <Lock className="h-4 w-4" /> {busy ? "Opening secure payment…" : "Try payment again"}
                </button>
                <button onClick={load} disabled={busy}
                        className="w-full h-11 rounded-full border hairline text-white/85 text-sm inline-flex items-center justify-center gap-2 hover:text-gold hover:border-gold/60 disabled:opacity-40">
                    <RotateCcw className="h-4 w-4" /> Check payment status
                </button>
                <p className="text-center text-[11px] text-white/45">
                    Still stuck?{" "}
                    <a href={PHONE_HREF} target="_blank" rel="noopener noreferrer" className="text-gold hover:underline inline-flex items-center gap-1">
                        Message the salon <ExternalLink className="h-3 w-3" />
                    </a>
                </p>
            </div>
        </Layout>
    );
}
